


import { makeStyles } from 'tss-react/mui';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Grid, KeyValue, MUIDataTable, MUIDataTableColumnDef, Typography } from 'web-library';
import { ResitReceiptSummary, ResitSummarySubjectInfoItem } from '../api/candidate/resit/model';
import FormHeader from './form-items/FormHeader';
import SubjectFeeDetail from './SubjectFeeDetail';

const useStyles = makeStyles()((theme) =>
({
  '@global': {
    '#subject-review-table .MuiTableCell-root': {
      '&[data-colindex="5"]': {
        width: 200,
      },
      '&[data-colindex="6"]': {
        width: 200,
      },
    },
    '#subject-review-table .MuiTableCell-head': {
      '&[data-colindex="4"]': {
        textAlign: 'right',
      },
      '&[data-colindex="5"]': {
        textAlign: 'right',
      },
    },
  },
  container: {
    marginBottom: theme.spacing(3),
  },
  subHeaderContainer: {},
  gridContainer: {
    marginBottom: theme.spacing(4),
  },
  priceGrid: {
    display: 'flex',
    alignItems: 'flex-end',
  },
  feeCell: {
    flex: 1,
    display: 'flex',
    justifyContent: 'flex-end',
  },
}));

const transformFunction = (
  candidateSubjects: ResitSummarySubjectInfoItem[]
): ResitReceiptSummary[] =>
  candidateSubjects.map((sub: ResitSummarySubjectInfoItem, idx: number) => ({
    id: idx,
    registered: false,
    subject: sub.nameDescription,
    subjectCode: sub.code,
    examLevel: sub.levelDescription,
    paperNo: sub.paperNumberDescription,
    examSeries: sub.examSeriesDescription,
    examFees: (sub.examFee as unknown) as number,
    gst: (sub.gst as unknown) as number,
  }));

const SelectedSubjectsValues = ({
  classes,
  id,
  isFeeCell,
  value,
}: {
  classes: Record<string,string>;
  id?: string;
  isFeeCell: boolean;
  value: string;
}) => (
  <Box className={isFeeCell ? classes.feeCell : ''}>
    <Typography id={id} variant="body1">
      {value}
    </Typography>
  </Box>
);

export interface SelectedSubjectsReviewProps {
  showGST?: boolean;
  examLevel?: string;
  examYear?: string;
  candidateSubjects?: ResitSummarySubjectInfoItem[] | [];
  gstRegNo?: string;
  totalExamFee?: string;
  totalFee?: string;
  totalGst?: string;
  totalPayable?: string;
  gstAbsorbed?: string;
}

const SelectedSubjectReview: React.FC<SelectedSubjectsReviewProps> = (
  props: SelectedSubjectsReviewProps
) => {
  const {
    showGST = false,
    candidateSubjects = [],
    examLevel = '',
    examYear = '',
    gstRegNo = '',
    totalExamFee = '0',
    totalFee = '0',
    totalGst = '0',
    totalPayable = '0',
    gstAbsorbed = '0.00',
  } = props;

  const { classes } = useStyles();
  const { t } = useTranslation();

  const constructSubjectInfo = useMemo(() => transformFunction(candidateSubjects), [
    candidateSubjects,
  ]);

  /** Datagrid columns
   * */
  const columns: MUIDataTableColumnDef[] = useMemo(
    () => [
      {
        name: 'subject',
        label: t('components->>SelectedSubjectReview.Subject'),

        disableClickEventBubbling: true,
        options: {
          filter: false,
          customBodyRenderLite: (dataIndex: number, rowIndex: number) =>
            SelectedSubjectsValues({
              classes,
              isFeeCell: false,
              value: constructSubjectInfo[dataIndex].subject,
            }),
        },
      },
      {
        name: 'subjectCode',
        label: t('components->>SelectedSubjectReview.Subject Code'),

        disableClickEventBubbling: true,
        options: {
          filter: false,
          customBodyRenderLite: (dataIndex: number, rowIndex: number) =>
            SelectedSubjectsValues({
              classes,
              id: `text-subjectCode-row${rowIndex}`,
              isFeeCell: false,
              value: constructSubjectInfo[dataIndex].subjectCode,
            }),
        },
      },
      {
        name: 'examSeries',
        label: t('components->>SelectedSubjectReview.Exam Series'),

        disableClickEventBubbling: true,
        options: {
          filter: false,
          customBodyRenderLite: (dataIndex: number, rowIndex: number) =>
            SelectedSubjectsValues({
              classes,
              isFeeCell: false,
              value: constructSubjectInfo[dataIndex].examSeries,
            }),
        },
      },
      {
        name: 'examLevel',
        label: t('components->>SelectedSubjectReview.Level'),

        disableClickEventBubbling: true,
        options: {
          filter: false,
          customBodyRenderLite: (dataIndex: number, rowIndex: number) =>
            SelectedSubjectsValues({
              classes,
              id: `text-examLevel-row${rowIndex}`,
              isFeeCell: false,
              value: constructSubjectInfo[dataIndex].examLevel || 'N/A',
            }),
        },
      },

      {
        name: 'gst',
        label: t('components->>SelectedSubjectReview.GST'),

        options: {
          display: showGST,
          filter: false,
          customBodyRenderLite: (dataIndex: number, rowIndex: number) =>
            SelectedSubjectsValues({
              classes,
              isFeeCell: true,
              value: constructSubjectInfo[dataIndex].gst.toString(),
            }),
        },
        disableClickEventBubbling: true,
      },
      {
        name: 'examFee',
        label: t('components->>SelectedSubjectReview.Exam Fees (SGD)'),
        disableClickEventBubbling: true,
        options: {
          filter: false,
          responsive: 'vertical',
          customBodyRenderLite: (dataIndex: number, rowIndex: number) =>
            SelectedSubjectsValues({
              classes,
              id: `text-examFees-row${rowIndex}`,
              isFeeCell: true,
              value: constructSubjectInfo[dataIndex].examFees.toString(),
            }),
        },
      },
    ],
    [classes, constructSubjectInfo, showGST, t]
  );

  return (
    (<Grid className={classes.container}>
      <FormHeader title={t('components->>SelectedSubjectReview.Selected Subject(s)/Paper(s)')} />
      <Grid container className={classes.subHeaderContainer} xs={12}>
        <Grid xs={12} sm={6} md={4} container direction="column">
          <KeyValue
            id="examLevel"
            keyText={t('components->>SelectedSubjectReview.Exam Level')}
            valueText={examLevel}
          />
        </Grid>
        <Grid xs={12} sm={6} md={4} container direction="column">
          <KeyValue
            id="examYear"
            keyText={t('components->>SelectedSubjectReview.Exam Year')}
            valueText={examYear}
          />
        </Grid>
      </Grid>
      <Grid className={classes.gridContainer} xs={12}>
        <Box id="subject-review-table">
          <MUIDataTable columns={columns} data={constructSubjectInfo}  />
        </Box>
      </Grid>
      <Grid container xs={12} justify="flex-start" direction="column">
        <KeyValue
          id="gstRegNo"
          keyText={t('components->>SelectedSubjectReview.GST Reg No.')}
          valueText={gstRegNo}
        />
      </Grid>
      <SubjectFeeDetail
        totalExamFee={totalExamFee}
        totalGst={totalGst}
        totalFee={totalFee}
        totalPayable={totalPayable}
        gstAbsorbed={gstAbsorbed}
      />
    </Grid>)
  );
};
export default SelectedSubjectReview;
